import React, { useState, useCallback, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { US_STATES } from '../data/usLocations';
import { INTERPRETER_LANGUAGES } from '../data/interpreterLanguages';

interface AttachmentFormProps {
  initialValues?: any;
  onSubmit: (data: FormData) => void;
  loading?: boolean;
}

const AttachmentForm: React.FC<AttachmentFormProps> = ({
  initialValues = {},
  onSubmit,
  loading,
}) => {
  const isEditing = Boolean(initialValues?.id);

  const [name, setName] = useState(initialValues.name || '');
  const [emailCategory, setEmailCategory] = useState(
    initialValues.email_category || ''
  );
  const [interpreterLanguage, setInterpreterLanguage] = useState(
    initialValues.interpreter_language || ''
  );
  const [caseType, setCaseType] = useState(initialValues.case_type || '');
  const [caseState, setCaseState] = useState(initialValues.case_state || '');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    setName(initialValues.name || '');
    setEmailCategory(initialValues.email_category || '');
    setInterpreterLanguage(initialValues.interpreter_language || '');
    setCaseType(initialValues.case_type || '');
    setCaseState(initialValues.case_state || '');
    setFile(null);
  }, [initialValues]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', name);
    formData.append('email_category', emailCategory);
    formData.append('interpreter_language', interpreterLanguage);
    formData.append('case_type', caseType);
    formData.append('case_state', caseState);
    if (file) {
      formData.append('file', file);
    }
    onSubmit(formData);
  };

  return (
    <Box
      component='form'
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: 400 }}
    >
      <TextField
        label='Attachment Name'
        value={name}
        onChange={e => setName(e.target.value)}
        required
      />

      <FormControl fullWidth>
        <InputLabel id='email-category-label'>Email Category</InputLabel>
        <Select
          labelId='email-category-label'
          value={emailCategory}
          label='Email Category'
          onChange={e => {
            setEmailCategory(e.target.value);
            if (e.target.value !== 'interpreter') {
              setInterpreterLanguage('');
            }
          }}
        >
          <MenuItem value=''>No Category</MenuItem>
          <MenuItem value='no_conflict_email'>No Conflict Email</MenuItem>
          <MenuItem value='retainer_received'>Retainer Received</MenuItem>
          <MenuItem value='interpreter'>
            Interpreter Documents (No Conflict Email)
          </MenuItem>
        </Select>
      </FormControl>

      {emailCategory === 'interpreter' && (
        <>
          <FormControl fullWidth required>
            <InputLabel id='interpreter-language-label'>Interpreter Language</InputLabel>
            <Select
              labelId='interpreter-language-label'
              value={interpreterLanguage}
              label='Interpreter Language'
              onChange={e => setInterpreterLanguage(e.target.value)}
            >
              {INTERPRETER_LANGUAGES.map(language => (
                <MenuItem key={language.code} value={language.code}>
                  {language.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant='caption' color='text.secondary'>
            Attached to the No Conflict (case approval) email when the matching
            language is selected at case creation.
          </Typography>
        </>
      )}

      <FormControl fullWidth>
        <InputLabel id='case-type-label'>Case Type</InputLabel>
        <Select
          labelId='case-type-label'
          value={caseType}
          label='Case Type'
          onChange={e => setCaseType(e.target.value)}
        >
          <MenuItem value=''>Both (Plaintiff &amp; Defendant)</MenuItem>
          <MenuItem value='Plaintiff'>Plaintiff Only</MenuItem>
          <MenuItem value='Defendant'>Defendant Only</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel id='case-state-label'>State of Case</InputLabel>
        <Select
          labelId='case-state-label'
          value={caseState}
          label='State of Case'
          onChange={e => setCaseState(e.target.value)}
        >
          <MenuItem value=''>All States</MenuItem>
          {US_STATES.map(state => (
            <MenuItem key={state.abbreviation} value={state.abbreviation}>
              {state.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {emailCategory === 'no_conflict_email' && (
        <Typography variant='caption' color='text.secondary'>
          When a state is selected, this document is attached to the No Conflict
          email only for cases in that state. Leave as &quot;All States&quot; to
          include for every case.
        </Typography>
      )}

      <Paper
        {...getRootProps()}
        sx={{
          border: '2px dashed #ccc',
          padding: 2,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa',
        }}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <Typography>Drop the file here...</Typography>
        ) : file ? (
          <Typography>Selected file: {file.name}</Typography>
        ) : isEditing ? (
          <Typography>
            Current file kept. Drop or click to replace the file (optional).
          </Typography>
        ) : (
          <Typography>
            Drag &amp; drop a file here, or click to select a file
          </Typography>
        )}
      </Paper>

      <Button type='submit' variant='contained' disabled={loading}>
        {loading ? 'Saving...' : isEditing ? 'Save Changes' : 'Submit'}
      </Button>
    </Box>
  );
};

export default AttachmentForm;
